/**
 * Sesión B WF2 — Suite activation request dialog (advisor surface).
 * Phase 3 — Advisors see the COMPLETE canonical suite catalog. Requesting a
 * non-contracted suite creates a contract/entitlement request; requesting a
 * contracted-but-inactive suite creates an activation request. Both require
 * HM Admin approval.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Info } from 'lucide-react';
import { toast } from 'sonner';
import { requestsApi } from '@/lib/adminApi';

const SUITE_CODES = [
  'talentmap', 'talentcheck', 'talentstart', 'talentheart', 'talentfix',
  'climara', 'talentcompetency', 'talenttrain', 'intelligence',
  'workforce_records',
];
const URGENCIES = ['low', 'medium', 'high', 'urgent'];

export default function SuiteRequestDialog({
  open, onOpenChange, company, requesterName,
  activeSuites = [], contractedSuites = [], onSubmitted,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    suite_code: '', justification: '', use_case: '', urgency: 'medium',
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const contracted = new Set([...(contractedSuites || []), ...(activeSuites || [])]);

  const submit = async () => {
    if (!form.suite_code) { toast.error(t('pages.requests.suiteForm.pickSuite')); return; }
    if (form.justification.length < 4 || form.use_case.length < 4) {
      toast.error(t('pages.requests.suiteForm.validationMissing'));
      return;
    }
    setSaving(true);
    try {
      const doc = await requestsApi.createSuiteRequest({
        ...form,
        company_id: company?.id,
      });
      toast.success(t('pages.requests.suiteForm.submitted'));
      onSubmitted?.(doc);
      onOpenChange(false);
      setForm({ suite_code: '', justification: '', use_case: '', urgency: 'medium' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('pages.requests.suiteForm.submitFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Phase 3 — full catalog; only ACTIVE suites cannot be requested.
  const stateOf = (code) => {
    if ((activeSuites || []).includes(code)) return 'active';
    if (contracted.has(code)) return 'contracted';
    return 'not_contracted';
  };
  const selectable = SUITE_CODES.filter((c) => stateOf(c) !== 'active');
  const selectedState = form.suite_code ? stateOf(form.suite_code) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="suite-request-dialog">
        <DialogHeader>
          <DialogTitle>{t('pages.requests.suiteForm.title')}</DialogTitle>
          <DialogDescription>
            {company?.name ? `${company.name} — ` : ''}{t('pages.requests.suiteForm.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label>{t('pages.requests.suiteForm.suite')} *</Label>
            <Select value={form.suite_code} onValueChange={(v) => set('suite_code', v)}>
              <SelectTrigger data-testid="suite-req-suite"><SelectValue placeholder={t('pages.requests.suiteForm.pickSuitePh')} /></SelectTrigger>
              <SelectContent>
                {selectable.length === 0 && (
                  <SelectItem value="__none__" disabled>{t('pages.requests.suiteForm.allAlreadyActive')}</SelectItem>
                )}
                {selectable.map((code) => (
                  <SelectItem key={code} value={code}>
                    <span className="flex items-center gap-2">
                      {t(`suite.names.${code}`, { defaultValue: code })}
                      <span className="text-xs text-muted-foreground">
                        {stateOf(code) === 'contracted'
                          ? `· ${t('phase3.suites.contractedInactive', { defaultValue: 'Contracted — inactive' })}`
                          : `· ${t('phase3.suites.notContracted', { defaultValue: 'Not contracted' })}`}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1.5" data-testid="suite-req-ge-note">
              {t('phase3.suites.geNote', { defaultValue: 'Global Ethics is provisioned separately through its dedicated request/approval flow — it is not part of this catalog.' })}
            </p>
          </div>
          {selectedState && (
            <div
              className="flex items-start gap-2 text-xs rounded-md border border-primary/30 bg-primary/5 p-2.5"
              data-testid="suite-req-type-hint"
            >
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-primary" />
              <div>
                <Badge variant="outline" className="text-xs mb-1">
                  {selectedState === 'contracted'
                    ? t('phase3.suites.typeActivation', { defaultValue: 'Activation request' })
                    : t('phase3.suites.typeContract', { defaultValue: 'New contract request' })}
                </Badge>
                <p className="text-muted-foreground">
                  {selectedState === 'contracted'
                    ? t('phase3.suites.requestTypeActivation', { defaultValue: 'This suite is already contracted but inactive. HM Admin approval will activate it.' })
                    : t('phase3.suites.requestTypeContract', { defaultValue: 'This suite is not yet contracted. HM Admin approval will grant the entitlement and activate it.' })}
                </p>
              </div>
            </div>
          )}
          <div>
            <Label>{t('pages.requests.suiteForm.justification')} *</Label>
            <Textarea
              rows={3}
              value={form.justification}
              onChange={(e) => set('justification', e.target.value)}
              data-testid="suite-req-justification"
              placeholder={t('pages.requests.suiteForm.justificationPh')}
            />
          </div>
          <div>
            <Label>{t('pages.requests.suiteForm.useCase')} *</Label>
            <Textarea
              rows={3}
              value={form.use_case}
              onChange={(e) => set('use_case', e.target.value)}
              data-testid="suite-req-use-case"
              placeholder={t('pages.requests.suiteForm.useCasePh')}
            />
          </div>
          <div>
            <Label>{t('pages.requests.suiteForm.urgency')}</Label>
            <Select value={form.urgency} onValueChange={(v) => set('urgency', v)}>
              <SelectTrigger data-testid="suite-req-urgency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {URGENCIES.map((p) => (
                  <SelectItem key={p} value={p}>{t(`pages.requests.priority.${p}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('pages.requests.suiteForm.requestedBy')}</Label>
            <Input value={requesterName || ''} readOnly className="bg-muted" data-testid="suite-req-requester" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="suite-req-cancel">
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="suite-req-submit">
            {saving ? t('common.saving') : t('pages.requests.suiteForm.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
